import React, { useState, useEffect, useMemo } from 'react';
import { Heart, Phone, UserCheck, Save, Plus, List, Upload, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { PostInterestedUsers } from './apis/post';
import { GetInterstedUsers } from './apis/get';
import { Chip } from '@mui/material';
import { List as VirtualizedList, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import * as XLSX from 'xlsx';

interface User {
  Id?: number;
  Name?: string;
  name?: string;
  PhoneNumber?: string;
  phoneNumber?: string;
  IsInterested?: boolean;
  isInterested?: boolean;
  Exist?: boolean;
  CreatedAt?: string;
}

interface ListChildComponentProps {
  index: number;
  style: React.CSSProperties;
  data: User[];
}

const InterestedUsers: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userName, setUserName] = useState('');
  const [isInterested, setIsInterested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('form');
  const [excelLoading, setExcelLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'interested' | 'not-interested'>('all');

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const name = (user.Name || user.name || '').toLowerCase();
      const phone = (user.PhoneNumber || user.phoneNumber || '').toString();
      const isInterested = user.IsInterested || user.isInterested;
      
      // Search filter (name or phone)
      const matchesSearch = searchTerm === '' || 
        name.includes(searchTerm.toLowerCase()) || 
        phone.includes(searchTerm);
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'interested' && isInterested) ||
        (statusFilter === 'not-interested' && !isInterested);
      
      return matchesSearch && matchesStatus;
    });
  }, [users, searchTerm, statusFilter]);

  const existingUsersCount = users.filter(user => user.Exist).length;

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setExcelLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const normalizeKey = (obj: any) => {
        const normalized: any = {};
        Object.keys(obj).forEach(key => {
          const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
          normalized[normalizedKey] = obj[key];
        });
        return normalized;
      };

      const parseBoolean = (value: any): boolean => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const lower = value.toLowerCase().trim();
          return lower === 'true' || lower === '1' || lower === 'yes';
        }
        return Boolean(value);
      };

      for (const row of jsonData as any[]) {
        const normalizedRow = normalizeKey(row);
        
        const phoneNumber = normalizedRow.phonenumber || normalizedRow.phone || normalizedRow.mobile;
        const name = normalizedRow.name || normalizedRow.username;
        const isInterested = parseBoolean(normalizedRow.isinterested || normalizedRow.interested || false);

        if (phoneNumber && name) {
          await PostInterestedUsers({
            phoneNumber: phoneNumber.toString(),
            name: name.toString(),
            isInterested: isInterested ? 1 : 0
          });
        }
      }

      toast.success(`Successfully uploaded ${jsonData.length} users`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to process Excel file');
    } finally {
      setExcelLoading(false);
      event.target.value = '';
    }
  };

  const fetchUsers = async () => {
    try {
      setTableLoading(true);
      const response = await GetInterstedUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async () => {
    if (!phoneNumber || !userName) {
      toast.error('Phone number and name are required');
      return;
    }

    setLoading(true);
    try {
      const response = await PostInterestedUsers({
        phoneNumber,
        name: userName,
        isInterested: isInterested ? 1 : 0
      });
      
      toast.success('User added successfully');
      setPhoneNumber('');
      setUserName('');
      setIsInterested(false);
      fetchUsers();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to add user';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full shadow-lg mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Interested Users</h1>
          <p className="text-gray-300 text-sm">Manage user interest status</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('form')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'form'
                  ? 'text-pink-600 border-b-2 border-pink-600 bg-pink-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Add User
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'upload'
                  ? 'text-pink-600 border-b-2 border-pink-600 bg-pink-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload Excel
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'list'
                  ? 'text-pink-600 border-b-2 border-pink-600 bg-pink-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <List className="w-4 h-4 inline mr-2" />
              Users List ({filteredUsers.length})
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'form' ? (
            <div className="p-8">
              <div className="max-w-lg mx-auto space-y-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <Phone className="w-4 h-4 text-pink-500" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 focus:bg-white transition-all duration-200 text-gray-900"
                    placeholder="Enter phone number"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <UserCheck className="w-4 h-4 text-purple-500" />
                    Name
                  </label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:bg-white transition-all duration-200 text-gray-900"
                    placeholder="Enter user name"
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Heart className={`w-5 h-5 ${isInterested ? 'text-pink-500' : 'text-gray-400'}`} />
                      <div>
                        <span className="font-medium text-gray-700">Interest Status</span>
                        <p className="text-sm text-gray-500">Mark if user is interested</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInterested}
                        onChange={(e) => setIsInterested(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-14 h-7 rounded-full transition-all duration-300 ${isInterested ? 'bg-gradient-to-r from-pink-500 to-purple-500 shadow-lg' : 'bg-gray-300'}`}>
                        <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${isInterested ? 'translate-x-7' : 'translate-x-0.5'} mt-0.5`}></div>
                      </div>
                    </label>
                  </div>
                </div>

                <button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-3 px-4 rounded-xl font-medium hover:from-pink-600 hover:to-purple-700 transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : activeTab === 'upload' ? (
            <div className="p-8">
              <div className="max-w-lg mx-auto space-y-6">
                <div className="text-center">
                  <Upload className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Upload Excel File</h3>
                  <p className="text-sm text-gray-600 mb-6">Upload an Excel file with columns: phoneNumber, name, isInterested</p>
                </div>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-pink-400 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    disabled={excelLoading}
                    className="hidden"
                    id="excel-upload"
                  />
                  <label
                    htmlFor="excel-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm font-medium text-gray-700">
                      {excelLoading ? 'Processing...' : 'Click to upload Excel file'}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">Supports .xlsx and .xls files</span>
                  </label>
                </div>

                <div className="bg-blue-50 p-4 rounded-xl">
                  <h4 className="font-medium text-blue-800 mb-2">Excel Format Requirements:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Column 1: phoneNumber (required)</li>
                    <li>• Column 2: name (required)</li>
                    <li>• Column 3: isInterested (true/false or 1/0)</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Warning Message for Existing Users */}
              {existingUsersCount > 0 && (
                <div className="p-4 bg-orange-50 border-l-4 border-orange-400 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-orange-700">
                        <strong>Notice:</strong> {existingUsersCount} highlighted user{existingUsersCount > 1 ? 's' : ''} already exist{existingUsersCount === 1 ? 's' : ''} in the database and will be automatically removed from this list tomorrow.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Search and Filter Controls */}
              <div className="p-4 bg-gray-50 border-b space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search Input */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search by name or phone number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-900"
                    />
                  </div>
                  
                  {/* Status Filter */}
                  <div className="flex items-center gap-2">
                    <Filter className="text-gray-400 w-4 h-4" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as 'all' | 'interested' | 'not-interested')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-900"
                    >
                      <option value="all">All Status</option>
                      <option value="interested">Interested</option>
                      <option value="not-interested">Not Interested</option>
                    </select>
                  </div>
                </div>
                
                {/* Results Count */}
                <div className="text-sm text-gray-600">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b font-medium text-gray-700">
                <div>Name</div>
                <div>Phone</div>
                <div>Status</div>
              </div>
              
              {tableLoading ? (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  Loading...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <Search className="w-12 h-12 mb-2 text-gray-300" />
                  <p>No users found</p>
                  {searchTerm && <p className="text-sm">Try adjusting your search or filter</p>}
                </div>
              ) : (
                <div style={{ height: '400px' }}>
                  <AutoSizer>
                    {({ height, width }) => (
                      <VirtualizedList
                        height={height}
                        width={width}
                        rowCount={filteredUsers.length}
                        rowHeight={60}
                        rowRenderer={({ index, key, style }) => {
                          const user = filteredUsers[index];
                          const isExisting = user.Exist;
                          return (
                            <div 
                              key={key} 
                              style={style} 
                              className={`grid grid-cols-3 gap-4 p-4 border-b hover:bg-gray-50 items-center ${
                                isExisting ? 'bg-orange-50 border-orange-200' : ''
                              }`}
                            >
                              <div className={`text-gray-900 ${isExisting ? 'text-orange-800 font-medium' : ''}`}>
                                {user.Name || user.name}
                                {isExisting && <span className="ml-2 text-xs text-orange-600">(Existing)</span>}
                              </div>
                              <div className={`text-gray-700 ${isExisting ? 'text-orange-700' : ''}`}>
                                {user.PhoneNumber || user.phoneNumber}
                              </div>
                              <div>
                                <Chip 
                                  label={user.IsInterested || user.isInterested ? 'Interested' : 'Not Interested'}
                                  color={user.IsInterested || user.isInterested ? 'success' : 'default'}
                                  size="small"
                                  sx={{
                                    backgroundColor: user.IsInterested || user.isInterested ? '#dcfce7' : '#f3f4f6',
                                    color: user.IsInterested || user.isInterested ? '#166534' : '#6b7280'
                                  }}
                                />
                              </div>
                            </div>
                          );
                        }}
                      />
                    )}
                  </AutoSizer>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterestedUsers;